import { useCallback } from 'react'
import type { UseStreamConnectionReturn, RawActivityEventPayload } from '@/infra'
import { useStreamConnection, isRawActivityEventPayload } from '@/infra'
import { ACTIVITY_STREAM_CONFIG } from '@/index'

const devTools = import.meta.env.DEV
  ? await import('@dev-tools/knownTools').then(async (knownTools) => ({
      isKnownTool: knownTools.isKnownTool,
      collectUnknownToolSchema: (await import('@dev-tools/unknownToolSchemaCollector'))
        .collectUnknownToolSchema,
    }))
  : null

export interface UseActivityEventsStreamOptions {
  readonly accessToken: string | undefined
  readonly enabled: boolean
  readonly onEvent?: ((event: RawActivityEventPayload) => void) | undefined
  readonly onConnected?: ((connectionId: string) => void) | undefined
  readonly onDisconnected?: (() => void) | undefined
  readonly onError?: ((error: Error) => void) | undefined
}

export function useActivityEventsStream(
  options: UseActivityEventsStreamOptions,
): UseStreamConnectionReturn {
  const { accessToken, enabled, onEvent, onConnected, onDisconnected, onError } = options

  const handleEvent = useCallback(
    (data: string) => {
      try {
        const parsed: unknown = JSON.parse(data)
        if (isRawActivityEventPayload(parsed)) {
          // Dev-only: collect unknown tool schemas
          const toolName =
            typeof parsed.rawHookPayload['tool_name'] === 'string'
              ? parsed.rawHookPayload['tool_name']
              : null
          const toolInput = parsed.rawHookPayload['tool_input']

          if (
            devTools &&
            toolName &&
            toolInput &&
            typeof toolInput === 'object' &&
            !devTools.isKnownTool(toolName)
          ) {
            devTools.collectUnknownToolSchema(
              toolName,
              toolInput as Record<string, unknown>,
              parsed.eventType,
            )
          }

          onEvent?.(parsed)
        }
      } catch {
        // Silently ignore malformed events
      }
    },
    [onEvent],
  )

  const handleError = useCallback(
    (error: { message: string }) => {
      onError?.(new Error(error.message))
    },
    [onError],
  )

  return useStreamConnection({
    config: ACTIVITY_STREAM_CONFIG,
    accessToken,
    enabled,
    onEvent: handleEvent,
    onConnected,
    onDisconnected,
    onError: handleError,
  })
}
